import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Paper, Divider, Tooltip, Button, Alert, Chip, IconButton } from '@mui/material';
import { Receipt, Add, CheckCircle, EditOutlined, History } from '@mui/icons-material';
import IosShareIcon from '@mui/icons-material/IosShare';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WhiteFab from '../common/WhiteFab';
import { format, formatISODate, capitalize } from '../../common/utils/common.util';
import { BaseInvoiceDTO, PastInvoicesProps, CreateInvoiceDTO, EditInvoiceValues } from './invoice.type';
import CreatePastInvoiceDialog from './CreatePastInvoiceDialog';
import EditPastInvoiceDialog from './EditPastInvoiceDialog';
import InvoiceHistoryDialog from './InvoiceHistoryDialog';
import NumericFormatInput from '../general/NumericFormatInput';
import CustomizedTextField from '../project/CustomizedTextField';
import { sortInvoicesByNumberDesc } from './invoice.util';

const PastInvoices: React.FC<PastInvoicesProps> = ({
  invoices,
  onDeleteInvoice,
  isExportMode = false,
  isMonitoringReport = false,
  onAddPastInvoice,
  onExportInvoice,
  onUpdatePaidAmount,
  onEditInvoice,
  projectData,
  service,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<BaseInvoiceDTO | null>(null);
  const [historyInvoice, setHistoryInvoice] = useState<BaseInvoiceDTO | null>(null);
  const [editingAmounts, setEditingAmounts] = useState<Record<string, string>>({});
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const calculateNetOwed = (invoiceAmount: number, paidAmount: number) => invoiceAmount - paidAmount;

  // Filter out current invoice for monitoring reports; sort past invoices by invoice number suffix (desc), falling back to createdOn date (desc)
  const filteredAndSortedInvoices = useMemo(
    () => sortInvoicesByNumberDesc(invoices.filter(invoice => !isMonitoringReport || !(invoice as any).currentInvoice)),
    [invoices, isMonitoringReport],
  );

  // Sync local editing state when invoices change from outside
  useEffect(() => {
    setEditingAmounts(prev => {
      const updated = { ...prev };
      filteredAndSortedInvoices.forEach(inv => {
        // Only update if not currently being edited (no focus)
        if (!(inv.id in updated)) {
          updated[inv.id] = inv.invoice.paidAmount?.toString() || '0';
        }
      });
      return updated;
    });
  }, [filteredAndSortedInvoices]);

  const hasUnpaidInvoices =
    !isExportMode &&
    isMonitoringReport &&
    filteredAndSortedInvoices.length > 0 &&
    filteredAndSortedInvoices.some(inv => !inv.invoice.paidAmount || inv.invoice.paidAmount === 0);

  const handleExportInvoice = (invoice: BaseInvoiceDTO) => {
    if (!onExportInvoice) return;
    const exportData: CreateInvoiceDTO = {
      invoice: {
        number: invoice.invoice.number,
        invoiceDate: invoice.invoice.invoiceDate,
        invoiceAmount: invoice.invoice.invoiceAmount,
        paidAmount: invoice.invoice.paidAmount,
        comment: invoice.invoice.comment,
      },
      recepient: invoice.recepient,
      recepientAddress: invoice.recepientAddress,
      projectName: invoice.projectName,
      projectAddress: invoice.projectAddress,
      service: invoice.service,
    };
    onExportInvoice(exportData);
  };

  const handlePaidAmountBlur = async (invoiceId: string, currentPaidAmount: number, invoiceAmount: number) => {
    if (!onUpdatePaidAmount) return;
    const rawValue = editingAmounts[invoiceId];
    const newAmount = parseFloat(rawValue) || 0;
    if (newAmount > invoiceAmount) {
      setEditingAmounts(prev => ({ ...prev, [invoiceId]: currentPaidAmount.toString() }));
      return;
    }
    if (newAmount === currentPaidAmount) return;

    setSavingIds(prev => new Set(prev).add(invoiceId));
    try {
      await onUpdatePaidAmount(invoiceId, newAmount);
    } catch {
      setEditingAmounts(prev => ({ ...prev, [invoiceId]: currentPaidAmount.toString() }));
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  const handleMarkAsPaid = async (invoice: BaseInvoiceDTO) => {
    if (!onUpdatePaidAmount) return;
    const fullAmount = invoice.invoice.invoiceAmount;
    const previousAmount = invoice.invoice.paidAmount;
    setEditingAmounts(prev => ({ ...prev, [invoice.id]: fullAmount.toString() }));
    setSavingIds(prev => new Set(prev).add(invoice.id));
    try {
      await onUpdatePaidAmount(invoice.id, fullAmount);
    } catch {
      setEditingAmounts(prev => ({ ...prev, [invoice.id]: previousAmount.toString() }));
    } finally {
      setSavingIds(prev => {
        const next = new Set(prev);
        next.delete(invoice.id);
        return next;
      });
    }
  };

  const handleEditInvoice = async (invoiceId: string, values: EditInvoiceValues) => {
    if (!onEditInvoice) return;
    await onEditInvoice(invoiceId, values);
    setEditingAmounts(prev => ({ ...prev, [invoiceId]: values.paidAmount.toString() }));
  };

  const isFullyPaid = (invoice: BaseInvoiceDTO) =>
    invoice.invoice.paidAmount > 0 && invoice.invoice.paidAmount >= invoice.invoice.invoiceAmount;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: hasUnpaidInvoices ? 1 : 2 }}>
        <Typography variant='h6'>
          {filteredAndSortedInvoices.length > 0
            ? `${isMonitoringReport ? 'Past Invoices' : 'History'} (${filteredAndSortedInvoices.length})`
            : isMonitoringReport
              ? 'No Past Invoices'
              : 'No History'}
        </Typography>

        {isMonitoringReport && !isExportMode && (
          <Tooltip title='Add Past Invoice'>
            <WhiteFab size='small' icon={<Add fontSize='small' />} onClick={() => setIsDialogOpen(true)} textColor='primary' />
          </Tooltip>
        )}
      </Box>

      {hasUnpaidInvoices && (
        <Alert severity='warning' sx={{ mb: 2, fontSize: '0.8rem' }}>
          Remember to mark past invoices as paid before moving on.
        </Alert>
      )}

      {filteredAndSortedInvoices.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Receipt sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant='body1'>No past invoices available.</Typography>
          {!isMonitoringReport && !isExportMode && (
            <Typography variant='body2'>Click the plus button to add your first invoice.</Typography>
          )}
        </Box>
      ) : (
        <Paper elevation={1} sx={{ p: 0, mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          {filteredAndSortedInvoices.map((invoice, index) => (
            <React.Fragment key={invoice.id}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems='flex-start'>
                  {/* Invoice # */}
                  <Grid size={{ xs: 2 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Invoice #
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant='body2' fontWeight='medium'>
                        {invoice.invoice.number}
                      </Typography>
                      <Tooltip
                        title={
                          <span>
                            <b>Recipient:</b>
                            <br />
                            {invoice.recepient || '--'},<br />
                            {invoice.recepientAddress?.street || '--'}
                            <br />
                            {invoice.recepientAddress?.city || '--'},{' '}
                            {invoice.recepientAddress?.state ? capitalize(invoice.recepientAddress.state) : '--'}
                            {invoice.recepientAddress?.postalCode ? ', ' + invoice.recepientAddress.postalCode : ''}
                            <br />
                            <br />
                            <b>Project:</b>
                            <br />
                            {invoice.projectName || '--'},<br />
                            {invoice.projectAddress?.street || '--'}
                            <br />
                            {invoice.projectAddress?.city || '--'},{' '}
                            {invoice.projectAddress?.state ? capitalize(invoice.projectAddress.state) : '--'}
                            {invoice.projectAddress?.postalCode ? ', ' + invoice.projectAddress.postalCode : ''}
                          </span>
                        }
                        arrow
                        placement='top'
                      >
                        <InfoOutlinedIcon color='action' sx={{ cursor: 'pointer', fontSize: 16 }} />
                      </Tooltip>
                      {invoice.invoice.comment && (
                        <Tooltip title={invoice.invoice.comment} arrow placement='top'>
                          <ChatBubbleOutlineIcon color='action' sx={{ cursor: 'pointer', fontSize: 16 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>

                  {/* Date */}
                  <Grid size={{ xs: 1.5 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Date
                    </Typography>
                    <Typography variant='body2'>{formatISODate(invoice.invoice.invoiceDate || '') || '--'}</Typography>
                  </Grid>

                  {/* Amount Due */}
                  <Grid size={{ xs: 2 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Amount
                    </Typography>
                    <Typography variant='body2'>{format(invoice.invoice.invoiceAmount.toString(), 'currency')}</Typography>
                  </Grid>

                  {/* Amount Paid — editable in edit mode */}
                  <Grid size={{ xs: 2.5 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Amount Paid
                    </Typography>
                    {!isExportMode && onUpdatePaidAmount ? (
                      isFullyPaid(invoice) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <Typography variant='body2' fontWeight='medium'>
                            {format(invoice.invoice.paidAmount.toString(), 'currency')}
                          </Typography>
                          <Tooltip title='Paid' placement='top'>
                            <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mt: 0.25 }}>
                          <CustomizedTextField
                            value={editingAmounts[invoice.id] ?? invoice.invoice.paidAmount.toString()}
                            onChange={e => setEditingAmounts(prev => ({ ...prev, [invoice.id]: e.target.value }))}
                            onBlur={() => handlePaidAmountBlur(invoice.id, invoice.invoice.paidAmount, invoice.invoice.invoiceAmount)}
                            InputProps={{ inputComponent: NumericFormatInput as any }}
                            size='small'
                            disabled={savingIds.has(invoice.id)}
                            error={(parseFloat(editingAmounts[invoice.id]) || 0) > invoice.invoice.invoiceAmount}
                            sx={{ width: 110 }}
                          />
                          {(parseFloat(editingAmounts[invoice.id]) || 0) > invoice.invoice.invoiceAmount ? (
                            <Typography variant='caption' color='error' sx={{ lineHeight: 1.2 }}>
                              Cannot exceed amount due
                            </Typography>
                          ) : (
                            <Chip
                              label='Mark as Paid'
                              size='small'
                              variant='outlined'
                              onClick={() => handleMarkAsPaid(invoice)}
                              disabled={savingIds.has(invoice.id)}
                              sx={{ fontSize: '0.68rem', height: 22, color: 'text.secondary', borderColor: 'divider', cursor: 'pointer' }}
                            />
                          )}
                        </Box>
                      )
                    ) : (
                      <Typography variant='body2'>{format(invoice.invoice.paidAmount.toString(), 'currency')}</Typography>
                    )}
                  </Grid>

                  {/* Net Owed */}
                  <Grid size={{ xs: 1.5 }}>
                    <Typography variant='body2' color='text.secondary'>
                      Net Owed
                    </Typography>
                    <Typography variant='body2' fontWeight='medium'>
                      {format(
                        calculateNetOwed(
                          invoice.invoice.invoiceAmount,
                          parseFloat(editingAmounts[invoice.id] ?? invoice.invoice.paidAmount.toString()) || invoice.invoice.paidAmount,
                        ).toString(),
                        'currency',
                      )}
                    </Typography>
                  </Grid>

                  {/* Actions */}
                  <Grid size={{ xs: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                      {!isExportMode && onEditInvoice && (
                        <Tooltip title='Edit invoice'>
                          <IconButton size='small' onClick={() => setEditingInvoice(invoice)} disabled={savingIds.has(invoice.id)}>
                            <EditOutlined fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isMonitoringReport && invoice.invoice.id && (
                        <Tooltip title='View history'>
                          <IconButton size='small' onClick={() => setHistoryInvoice(invoice)}>
                            <History fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title='Export as PDF'>
                        <Button
                          onClick={() => handleExportInvoice(invoice)}
                          size='small'
                          variant='text'
                          startIcon={<IosShareIcon />}
                          sx={{ color: 'primary.main', minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                        >
                          Export as PDF
                        </Button>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              {index < filteredAndSortedInvoices.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Paper>
      )}

      {isMonitoringReport && onAddPastInvoice && (
        <CreatePastInvoiceDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={onAddPastInvoice}
          projectData={projectData}
          service={service}
          invoices={invoices}
        />
      )}
      {onEditInvoice && (
        <EditPastInvoiceDialog
          open={!!editingInvoice}
          invoice={editingInvoice}
          invoices={invoices}
          onClose={() => setEditingInvoice(null)}
          onSave={handleEditInvoice}
        />
      )}
      <InvoiceHistoryDialog
        open={!!historyInvoice}
        invoiceId={historyInvoice?.invoice.id}
        invoiceNumber={historyInvoice?.invoice.number}
        onClose={() => setHistoryInvoice(null)}
      />
    </Box>
  );
};

export default PastInvoices;
